import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, Plus, MoreVertical, Download, Smartphone } from "lucide-react";
import BrainLogo from "@/components/BrainLogo";

const DISMISS_KEY = "sv_a2hs_dismissed_at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // re-prompt after 7 days

function detect() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const standalone =
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true;
  const smallScreen = window.matchMedia && window.matchMedia("(max-width: 820px)").matches;
  return { isIOS, isAndroid, standalone, isMobile: isIOS || isAndroid || smallScreen };
}

export default function AddToHomeScreen() {
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [env] = useState(detect);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  useEffect(() => {
    if (env.standalone || !env.isMobile) return;
    let dismissedAt = 0;
    try { dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10); } catch {}
    if (dismissedAt && Date.now() - dismissedAt < SNOOZE_MS) return;
    const t = setTimeout(() => setOpen(true), 3500);
    return () => clearTimeout(t);
  }, [env]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setOpen(false);
  };

  const androidInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch {}
      setDeferred(null);
      dismiss();
    }
  };

  if (env.standalone || !env.isMobile) return null;

  const iosSteps = [
    { icon: Share, label: "Tap the Share icon in your browser bar." },
    { icon: Plus, label: "Scroll down and tap “Add to Home Screen”." },
    { icon: Smartphone, label: "Tap “Add” — Studivault lands on your home screen." },
  ];
  const androidSteps = [
    { icon: MoreVertical, label: "Tap the ⋮ menu in the top-right of Chrome." },
    { icon: Download, label: "Tap “Install app” or “Add to Home screen”." },
    { icon: Smartphone, label: "Confirm — Studivault installs like a native app." },
  ];
  const steps = env.isIOS ? iosSteps : androidSteps;
  const canNativeInstall = env.isAndroid && !!deferred;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="a2hs-overlay"
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[3px]" onClick={dismiss} />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="relative w-full max-w-[480px] rounded-t-3xl p-6 pb-8"
            style={{
              background: "var(--sheet-bg, rgba(19,19,22,0.96))",
              borderTop: "1px solid var(--glass-border, rgba(255,255,255,0.10))",
              boxShadow: "0 -20px 60px -20px rgba(124,92,255,0.5)",
            }}
            data-testid="a2hs-sheet"
          >
            <button onClick={dismiss} className="btn-icon absolute top-4 right-4" data-testid="a2hs-close" aria-label="Close">
              <X size={16} />
            </button>

            <div className="flex items-center gap-3 mb-1">
              <div className="rounded-2xl overflow-hidden" style={{ boxShadow: "0 0 24px -6px rgba(124,92,255,0.7)" }}>
                <BrainLogo size={44} animate={false} />
              </div>
              <div>
                <h3 className="text-[19px] font-bold tracking-tight">Install Studivault</h3>
                <p className="text-[13px] text-[#B0AEBC]">Faster launch · full-screen · home-screen access.</p>
              </div>
            </div>

            {canNativeInstall && (
              <button
                onClick={androidInstall}
                className="btn btn-primary w-full mt-5 py-3.5 text-[15px]"
                data-testid="a2hs-install-btn"
              >
                <Download size={16} /> Install app
              </button>
            )}

            <div className="mt-5 space-y-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#B8A4FF]">
                {env.isIOS ? "Add to Home Screen — iPhone & iPad" : canNativeInstall ? "Or add it manually" : "Add to Home Screen — Android"}
              </div>
              {steps.map((s, i) => (
                <div key={i} className="flex items-center gap-3.5" data-testid={`a2hs-step-${i}`}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(124,92,255,0.14)", border: "1px solid rgba(124,92,255,0.3)" }}>
                    <s.icon size={16} className="text-[#B8A4FF]" />
                  </div>
                  <div className="flex items-center gap-2 text-[13.5px] text-[#E4E4E7]">
                    <span className="text-[#8E8B99] font-bold tabular-nums">{i + 1}</span>
                    <span>{s.label}</span>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={dismiss} className="btn btn-ghost w-full mt-5 py-2.5 text-[13px]" data-testid="a2hs-later">
              Maybe later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
